import React from 'react';
import {
    Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
    FormHelperText,
    Grid,
    MenuItem,
    TextField,
} from '@mui/material';
import { initialValuesInsurancePolicyForm, insurancePolicyValidationSchema } from './insurancePolicyValidationSchema'; // Import your validation schema
import { useFormik } from 'formik';
import { CustomerService } from '../../../../service/customerService';
import { toast } from 'react-toastify';
import ProviderAutocomplete from '../../../insurancePolicy/ProviderAutocomplete';

export default function CreateInsurancePolicyDialog({
    open,
    onClose,
    onCreated,
    customer
}) {
    const formik = useFormik({
        initialValues: {
            ...initialValuesInsurancePolicyForm
        },
        validationSchema: insurancePolicyValidationSchema,
        onSubmit: async (values) => {

            // call the addInsurancePolicy service 
            try {
                const payload = {
                    ...values,
                    providerId: values.provider?.id ?? null,
                };
                delete payload.provider;
                const response = await CustomerService.addInsurancePolicy(customer.customer.id, payload);
                const { code, message } = response;
                if (code === 0) {
                    // handle success
                    toast.success(message);
                    formik.resetForm();
                    if (onCreated) onCreated(response.data, values.provider);
                    onClose();

                } else {
                    // handle error
                    toast.error(message);
                    console.error('Error adding insurance policy:', response);
                }
            } catch (error) {
                // handle unexpected errors
                toast.error('An unexpected error occurred. Please try again.');
                console.error('Unexpected error:', error);
            }
        }
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add Insurance Policy</DialogTitle>
            <DialogContent dividers>
                <form onSubmit={formik.handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <ProviderAutocomplete
                                required
                                value={formik.values.provider}
                                onChange={(val) => formik.setFieldValue('provider', val)}
                                error={formik.touched.provider && Boolean(formik.errors.provider)}
                                helperText={formik.touched.provider && formik.errors.provider}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                id="policyNumber"
                                name="policyNumber"
                                label="Policy Number"
                                required
                                value={formik.values.policyNumber}
                                onChange={formik.handleChange}
                                error={formik.touched.policyNumber && Boolean(formik.errors.policyNumber)}
                                helperText={formik.touched.policyNumber && formik.errors.policyNumber}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                id="groupNumber"
                                name="groupNumber"
                                label="Group Number"
                                value={formik.values.groupNumber}
                                onChange={formik.handleChange}
                                error={formik.touched.groupNumber && Boolean(formik.errors.groupNumber)}
                                helperText={formik.touched.groupNumber && formik.errors.groupNumber}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                id="contactPhone"
                                name="contactPhone"
                                label="Phone"
                                value={formik.values.contactPhone}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                id="contactEmail"
                                name="contactEmail"
                                label="Email"
                                value={formik.values.contactEmail}
                                onChange={formik.handleChange}
                                error={formik.touched.contactEmail && Boolean(formik.errors.contactEmail)}
                                helperText={formik.touched.contactEmail && formik.errors.contactEmail}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                id="contactAddress"
                                name="contactAddress"
                                label="Address"
                                value={formik.values.contactAddress}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                id="contactCity"
                                name="contactCity"
                                label="City"
                                value={formik.values.contactCity}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                id="contactProvince"
                                name="contactProvince"
                                label="Province"
                                value={formik.values.contactProvince}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                id="contactPostal"
                                name="contactPostal"
                                label="Postal Code"
                                value={formik.values.contactPostal}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                id="contactCountry"
                                name="contactCountry"
                                label="Country"
                                value={formik.values.contactCountry}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                        {/* <Grid item xs={12}>
                            <TextField
                                fullWidth
                                id="policyHolderName"
                                name="policyHolderName"
                                label="Policy Holder Name"
                                value={formik.values.policyHolderName}
                                onChange={formik.handleChange}
                                error={formik.touched.policyHolderName && Boolean(formik.errors.policyHolderName)}
                                helperText={formik.touched.policyHolderName && formik.errors.policyHolderName}
                            />
                            <FormHelperText>If name is different than the customer</FormHelperText>
                        </Grid> */}
                        <Grid item xs={12}>
                            <TextField
                                select
                                fullWidth
                                id="status"
                                name="status"
                                label="Status"
                                value={formik.values.status}
                                onChange={formik.handleChange}
                            >
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="inactive">Inactive</MenuItem>
                            </TextField>
                        </Grid>
                        {
                            customer.customer.familyGroupId && (
                                <Grid item xs={12}>
                                    {/* checkbox to apply to all family members */}
                                    <Checkbox
                                        id="applyToAllFamilyMembers"
                                        name="applyToAllFamilyMembers"
                                        value={formik.values.applyToAllFamilyMembers}
                                        checked={formik.values.applyToAllFamilyMembers}
                                        onChange={formik.handleChange}
                                    />
                                    <label htmlFor="applyToAllFamilyMembers">Apply to all family members</label>
                                    <FormHelperText>If checked, this policy will be applied to all family members</FormHelperText>
                                </Grid>
                            )
                        }
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                id="notes"
                                name="notes"
                                label="Notes"
                                value={formik.values.notes}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                    </Grid>
                </form>

            </DialogContent>
            <DialogActions sx={{ boxShadow: 4 }}>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{ mt: 2 }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={formik.handleSubmit}
                    disabled={!formik.isValid || formik.isSubmitting}
                    sx={{ mt: 2 }}>
                    Save Policy
                </Button>
            </DialogActions>
        </Dialog>
    );
}